import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const PhotoUploadSection = ({
  photos = [],
  coverPhotoId,
  onUpload,
  onDelete,
  onSetCover,
  title = "Photos",
  description = "Upload and manage photos",
  maxFiles = 10,
  allowEdit = true,
  allowDelete = true,
  allowSetCover = true
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!onUpload) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      acceptedFiles.forEach((file) => {
        formData.append('photos', file);
      });

      await onUpload(formData, (progress) => {
        setUploadProgress(progress);
      });
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: maxFiles - photos.length,
    disabled: uploading || photos.length >= maxFiles
  });

  const handleEdit = (photo) => {
    setEditingPhoto(photo);
    setEditTitle(photo.title || '');
    setEditDescription(photo.description || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPhoto || !onUpload) return;

    try {
      // This would need to be implemented as a separate edit endpoint
      await onUpload({
        photoId: editingPhoto._id,
        title: editTitle,
        description: editDescription
      });
      setEditDialogOpen(false);
      setEditingPhoto(null);
    } catch (error) {
      console.error('Edit error:', error);
    }
  };

  const handleSetCover = (photoId) => {
    if (onSetCover && photoId !== coverPhotoId) {
      onSetCover(photoId);
    }
  };

  const handleDelete = (photoId) => {
    if (onDelete) {
      onDelete(photoId);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
      </Box>

      {/* Upload Area */}
      {photos.length < maxFiles && (
        <Box sx={{ mb: 3 }}>
          <Card
            {...getRootProps()}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              backgroundColor: isDragActive ? 'primary.light' : 'background.paper',
              cursor: uploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.light'
              }
            }}
          >
            <input {...getInputProps()} />
            <Box sx={{ textAlign: 'center' }}>
              <UploadIcon 
                sx={{ 
                  fontSize: 48, 
                  color: isDragActive ? 'primary.main' : 'grey.500',
                  mb: 2 
                }} 
              />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop photos here' : 'Upload Photos'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag and drop photos here, or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Accepted formats: JPEG, PNG, GIF, WebP ({photos.length}/{maxFiles} photos)
              </Typography>
            </Box>
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Uploading... {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* Photo Grid */}
      {photos.length > 0 && (
        <Grid container spacing={2}>
          {photos.map((photo) => (
            <Grid key={photo._id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card sx={{ position: 'relative' }}>
                {/* Cover Photo Indicator */}
                {photo._id === coverPhotoId && (
                  <Chip
                    icon={<StarIcon />}
                    label="Cover"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 1
                    }}
                  />
                )}

                <CardMedia
                  component="img"
                  height="200"
                  image={photo.imageUrl || photo.thumbnailUrl}
                  alt={photo.title || 'Photo'}
                  sx={{
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                />

                <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {allowSetCover && (
                      <IconButton
                        size="small"
                        onClick={() => handleSetCover(photo._id)}
                        color={photo._id === coverPhotoId ? 'primary' : 'default'}
                        title={photo._id === coverPhotoId ? 'Current cover photo' : 'Set as cover photo'}
                      >
                        {photo._id === coverPhotoId ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    )}
                    
                    {allowEdit && (
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(photo)}
                        title="Edit photo details"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>

                  {allowDelete && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(photo._id)}
                      title="Delete photo"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardActions>

                {(photo.title || photo.description) && (
                  <Box sx={{ p: 1, pt: 0 }}>
                    {photo.title && (
                      <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                        {photo.title}
                      </Typography>
                    )}
                    {photo.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {photo.description}
                      </Typography>
                    )}
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* No Photos Message */}
      {photos.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No photos uploaded yet. Upload some photos to showcase this item.
        </Alert>
      )}

      {/* Edit Photo Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Photo Details
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="Enter photo title"
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Enter photo description"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoUploadSection;